import { useState, useRef } from "react";
import { ClipboardList, Salad, Stethoscope, Import, Paperclip, Trash2, FileText, Eye, Contact, Pill, Activity } from "lucide-react";
import DietEditor from "@/components/lab/DietEditor";
import { makeDiet } from "@/lib/diet";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);

export const emptyAnamnesi = () => ({
  generali: { cellulare: "", email: "", disponibilita: "", luogo: "", obiettivo: "", attrezzi: "" },
  alimentare: { diet: makeDiet("Dieta Attuale"), allergie: "", intolleranze: "", pocoGraditi: "" },
  clinica: { infortuni: "", interventi: "", limitazioni: "", farmaci: "", dolori: "", referti: [] },
});

const Labeled = ({ label, children }) => (
  <div>
    <div className="field-label mb-1">{label}</div>
    {children}
  </div>
);

export default function AnamnesiTab({ anamnesi, setAnamnesi, foods, onCreateFood, onImportDiet, macroGrams }) {
  const A = anamnesi && anamnesi.alimentare ? anamnesi : emptyAnamnesi();
  const gen = A.generali || { cellulare: "", email: "" };
  const [sub, setSub] = useState("generali");
  const fileRef = useRef(null);

  const setGen = (patch) => setAnamnesi({ ...A, generali: { ...gen, ...patch } });
  const setAlim = (patch) => setAnamnesi({ ...A, alimentare: { ...A.alimentare, ...patch } });
  const setClin = (patch) => setAnamnesi({ ...A, clinica: { ...A.clinica, ...patch } });

  const importDiet = () => {
    onImportDiet(A.alimentare.diet);
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 3 * 1024 * 1024) {
        toast.error(`"${file.name}" supera 3 MB e non è stato allegato`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setAnamnesi((prev) => {
          const cur = prev && prev.clinica ? prev : A;
          return { ...cur, clinica: { ...cur.clinica, referti: [...(cur.clinica.referti || []), { id: uid(), name: file.name, type: file.type, dataUrl: reader.result }] } };
        });
      };
      reader.readAsDataURL(file);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeReferto = (id) =>
    setClin({ referti: (A.clinica.referti || []).filter((r) => r.id !== id) });

  const openReferto = (r) => {
    try {
      const [meta, b64] = r.dataUrl.split(",");
      const mime = (meta.match(/:(.*?);/) || [])[1] || r.type || "application/octet-stream";
      const bin = atob(b64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([u8], { type: mime }));
      const w = window.open(url, "_blank");
      if (!w) { toast.error("Consenti i popup per aprire l'allegato"); URL.revokeObjectURL(url); return; }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Impossibile aprire l'allegato");
    }
  };

  return (
    <div className="space-y-5" data-testid="tab-anamnesi">
      <div className="flex items-center gap-2">
        <h2 className="mr-2 flex items-center gap-2 font-display text-lg font-bold">
          <ClipboardList className="h-5 w-5 text-primary" /> Anamnesi
        </h2>
        <div className="flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
          {[["generali", "Info Generali", Contact], ["alimentare", "Alimentare", Salad], ["clinica", "Clinica", Stethoscope]].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setSub(v)} data-testid={`anamnesi-sub-${v}`}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${sub === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>
      </div>

      {sub === "generali" && (
        <div className="space-y-5" data-testid="anamnesi-generali">
          <section className="panel p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Labeled label="Numero di cellulare">
                <input type="tel" className="lab-input" placeholder="es. +39 333 1234567" value={gen.cellulare} onChange={(e) => setGen({ cellulare: e.target.value })} data-testid="cellulare-input" />
              </Labeled>
              <Labeled label="Email">
                <input type="email" className="lab-input" placeholder="es. mario.rossi@email.com" value={gen.email} onChange={(e) => setGen({ email: e.target.value })} data-testid="email-input" />
              </Labeled>
              <Labeled label="Data di nascita">
                <input type="date" className="lab-input" value={gen.data_nascita || ""} onChange={(e) => setGen({ data_nascita: e.target.value })} data-testid="data-nascita-input" />
              </Labeled>
              <Labeled label="Luogo di nascita">
                <input className="lab-input" placeholder="es. Torino" value={gen.luogo_nascita || ""} onChange={(e) => setGen({ luogo_nascita: e.target.value })} data-testid="luogo-nascita-input" />
              </Labeled>
              <Labeled label="Disponibilità allenamento">
                <select className="lab-input" value={gen.disponibilita || ""} onChange={(e) => setGen({ disponibilita: e.target.value })} data-testid="disponibilita-input">
                  <option value="">Seleziona…</option>
                  <option value="1 volta a settimana">1 volta a settimana</option>
                  <option value="2 volte a settimana">2 volte a settimana</option>
                  <option value="3 volte a settimana">3 volte a settimana</option>
                  <option value="4 volte a settimana">4 volte a settimana</option>
                  <option value="5 volte a settimana">5 volte a settimana</option>
                  <option value="6 volte a settimana">6 volte a settimana</option>
                  <option value="7 volte a settimana">7 volte a settimana</option>
                </select>
              </Labeled>
              <Labeled label="Luogo di allenamento">
                <input type="text" className="lab-input" placeholder="es. Palestra, Casa, All'aperto, Studio..." value={gen.luogo || ""} onChange={(e) => setGen({ luogo: e.target.value })} data-testid="luogo-input" />
              </Labeled>
              <Labeled label="Obiettivo">
                <input type="text" className="lab-input" placeholder="es. Dimagrimento, Massa muscolare, Ricomposizione, Tonificazione..." value={gen.obiettivo || ""} onChange={(e) => setGen({ obiettivo: e.target.value })} data-testid="obiettivo-input" />
              </Labeled>
              <Labeled label="Attrezzi disponibili">
                <textarea rows={3} className="lab-input resize-none" placeholder="es. manubri 2-20kg, panca regolabile, elastici, kettlebell, TRX... (utile se si allena a casa)" value={gen.attrezzi || ""} onChange={(e) => setGen({ attrezzi: e.target.value })} data-testid="attrezzi-input" />
              </Labeled>
            </div>
          </section>
        </div>
      )}

      {sub === "alimentare" && (
        <div className="space-y-5" data-testid="anamnesi-alimentare">
          <section className="panel p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Labeled label="Allergie">
                <textarea rows={3} className="lab-input resize-none" placeholder="es. arachidi, crostacei..." value={A.alimentare.allergie} onChange={(e) => setAlim({ allergie: e.target.value })} data-testid="allergie-input" />
              </Labeled>
              <Labeled label="Intolleranze">
                <textarea rows={3} className="lab-input resize-none" placeholder="es. lattosio, glutine..." value={A.alimentare.intolleranze} onChange={(e) => setAlim({ intolleranze: e.target.value })} data-testid="intolleranze-input" />
              </Labeled>
              <Labeled label="Alimenti poco graditi">
                <textarea rows={3} className="lab-input resize-none" placeholder="es. pesce, legumi, verdure amare... (da evitare nella dieta)" value={A.alimentare.pocoGraditi || ""} onChange={(e) => setAlim({ pocoGraditi: e.target.value })} data-testid="poco-graditi-input" />
              </Labeled>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-base font-bold text-primary">Dieta Attuale del Paziente</h3>
            <button onClick={importDiet} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="import-diet-btn">
              <Import className="h-4 w-4" /> Importa in Sezione 3
            </button>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            Registra la dieta che il paziente segue attualmente. Con "Importa" la copi nel Piano Alimentare (Sezione 3) per confrontarla mentre scrivi la nuova dieta.
          </p>

          <DietEditor
            diet={A.alimentare.diet}
            onChange={(d) => setAlim({ diet: d })}
            foods={foods}
            onCreateFood={onCreateFood}
            macroGrams={macroGrams}
            totalsLabel="TOTALE — DIETA ATTUALE"
          />
        </div>
      )}

      {sub === "clinica" && (
        <div className="space-y-5" data-testid="anamnesi-clinica">
          <section className="panel p-5">
            <div className="grid grid-cols-1 gap-4">
              <Labeled label="Infortuni">
                <textarea rows={3} className="lab-input resize-none" placeholder="Infortuni pregressi o in corso..." value={A.clinica.infortuni} onChange={(e) => setClin({ infortuni: e.target.value })} data-testid="infortuni-input" />
              </Labeled>
              <Labeled label="Interventi">
                <textarea rows={3} className="lab-input resize-none" placeholder="Interventi chirurgici..." value={A.clinica.interventi} onChange={(e) => setClin({ interventi: e.target.value })} data-testid="interventi-input" />
              </Labeled>
              <Labeled label="Limitazioni">
                <textarea rows={3} className="lab-input resize-none" placeholder="Limitazioni motorie, controindicazioni..." value={A.clinica.limitazioni} onChange={(e) => setClin({ limitazioni: e.target.value })} data-testid="limitazioni-input" />
              </Labeled>
            </div>
          </section>

          <section className="panel p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Labeled label={<span className="inline-flex items-center gap-1.5"><Pill className="h-4 w-4 text-primary" /> Farmaci</span>}>
                <textarea rows={4} className="lab-input resize-none" placeholder="Farmaci assunti, dosaggio e frequenza..." value={A.clinica.farmaci || ""} onChange={(e) => setClin({ farmaci: e.target.value })} data-testid="farmaci-input" />
              </Labeled>
              <Labeled label={<span className="inline-flex items-center gap-1.5"><Activity className="h-4 w-4 text-[hsl(var(--c-fat))]" /> Dolori ricorrenti</span>}>
                <textarea rows={4} className="lab-input resize-none" placeholder="Dolori ricorrenti, zona e frequenza (es. lombalgia, cervicale...)" value={A.clinica.dolori || ""} onChange={(e) => setClin({ dolori: e.target.value })} data-testid="dolori-input" />
              </Labeled>
            </div>
          </section>

          <section className="panel p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-base font-bold"><FileText className="h-4 w-4 text-primary" /> Referti Medici</h3>
              <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20" data-testid="attach-referto-btn">
                <Paperclip className="h-4 w-4" /> Allega file
              </button>
              <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={onFiles} data-testid="referto-file-input" />
            </div>
            {(A.clinica.referti || []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 py-8 text-center text-sm text-muted-foreground">Nessun referto allegato (immagini o PDF, max 3 MB)</div>
            ) : (
              <div className="space-y-2">
                {A.clinica.referti.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`referto-${r.id}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm">{r.name}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button onClick={() => openReferto(r)} className="rounded-md border border-white/10 p-1.5 text-muted-foreground hover:text-primary" title="Visualizza" data-testid={`view-referto-${r.id}`}><Eye className="h-4 w-4" /></button>
                      <button onClick={() => removeReferto(r.id)} className="rounded-md border border-white/10 p-1.5 text-muted-foreground hover:text-destructive" title="Rimuovi" data-testid={`remove-referto-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
